import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { Layout, Row, Col, Space, Typography, Divider } from 'antd';
import {
  GithubOutlined,
  LinkedinOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useSiteContent } from '@/context/ContentProvider';
import { trackExternalLink } from '@/utils/analytics';

const { Footer: AntFooter } = Layout;
const { Text } = Typography;

export const Footer: FC = () => {
  const currentYear = new Date().getFullYear();
  const siteContent = useSiteContent();
  const { footer, navbar, contact } = siteContent;

  const handleExternalClick = (url: string) => {
    trackExternalLink(url);
  };

  // Use navbar items minus Home for footer nav
  const footerNavItems = navbar.items.filter((item) => item.key !== '/');

  return (
    <AntFooter
      style={{
        padding: '64px 24px 32px',
        color: 'var(--color-footer-text)',
        minHeight: 320,
      }}
    >
      <div className="container">
        <Row gutter={[48, 48]}>
          <Col xs={24} md={8}>
            <Text
              strong
              style={{ fontSize: 20, color: 'var(--color-footer-heading)', display: 'block', marginBottom: 12 }}
            >
              {footer.brand}<span style={{ color: 'var(--color-accent)' }}>.</span>
            </Text>
            <Text style={{ color: 'var(--color-footer-text)', lineHeight: 1.8 }}>
              {footer.bio}
            </Text>
          </Col>

          <Col xs={24} md={8}>
            <Text
              strong
              style={{ color: 'var(--color-footer-heading)', display: 'block', marginBottom: 16 }}
            >
              {footer.navigationTitle}
            </Text>
            <Space orientation="vertical" size={8}>
              {footerNavItems.map((link) => (
                <Link
                  key={link.key}
                  to={link.key}
                  style={{ color: 'var(--color-footer-text)', fontSize: 14 }}
                >
                  {link.label}
                </Link>
              ))}
            </Space>
          </Col>

          <Col xs={24} md={8}>
            <Text
              strong
              style={{ color: 'var(--color-footer-heading)', display: 'block', marginBottom: 16 }}
            >
              {footer.connectTitle}
            </Text>
            <Space size={16}>
              <a
                href={contact.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleExternalClick('github')}
                aria-label="GitHub"
              >
                <GithubOutlined style={{ fontSize: 20, color: 'var(--color-footer-text)' }} />
              </a>
              <a
                href={contact.linkedinLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleExternalClick('linkedin')}
                aria-label="LinkedIn"
              >
                <LinkedinOutlined style={{ fontSize: 20, color: 'var(--color-footer-text)' }} />
              </a>
              <a
                href={`mailto:${contact.email}`}
                onClick={() => handleExternalClick('email')}
                aria-label="Email"
              >
                <MailOutlined style={{ fontSize: 20, color: 'var(--color-footer-text)' }} />
              </a>
            </Space>
          </Col>
        </Row>

        <Divider style={{ borderColor: 'var(--color-border)', margin: '40px 0 24px' }} />

        <Row justify="space-between" align="middle">
          <Col>
            <Text style={{ color: 'var(--color-footer-caption)', fontSize: 13 }}>
              &copy; {currentYear} {siteContent.meta.siteTitle.split('—')[0]?.trim()}. {footer.copyright}
            </Text>
          </Col>
          <Col>
            <Text style={{ color: 'var(--color-footer-caption)', fontSize: 13 }}>
              {footer.builtWith}
            </Text>
          </Col>
        </Row>
      </div>
    </AntFooter>
  );
};
